/**
 * B2G — the owner's side of the market-access seat's work: the country list
 * and the outreach waves (approve once, the server sends).
 *
 * Wire shapes mirror `system/services/markets.py :: serialize_market` and
 * `system/services/outreach/waves.py :: serialize_*` / `plan.py :: serialize_item` — change them together.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "./client";

export type MarketStatus = "long_list" | "active" | "paused" | "excluded";
export type TravelStatus = "none" | "safety_notes" | "regional_warning" | "advise_against" | "travel_warning";
export type TravelVerdict = "unknown" | "no_travel" | "owner_ok" | "ok";

export interface Market {
  id: string;
  iso2: string;
  name: string;
  status: MarketStatus;
  campaign_excluded: boolean;
  campaign_open: boolean;
  exclusion_reason: string | null;
  score: number | null;
  score_rationale: string | null;
  languages: string[];
  travel_status: TravelStatus | null;
  travel_verdict: TravelVerdict;
  travel_checked_at: string | null;
  travel_source_url: string | null;
  /** World Bank figures; the `*_vs_germany` fields are the share of Germany's (0.22 = 22 %). */
  population: number | null;
  gdp_per_capita_usd: number | null;
  gdp_per_capita_vs_germany: number | null;
  /** Population × GDP per capita (current US$), null without both. */
  gdp_usd: number | null;
  gdp_vs_germany: number | null;
  economy_year: number | null;
  ministry_company_id: string | null;
  primary_contact_id: string | null;
  note: string | null;
  decided_at: string | null;
  updated_at: string | null;
}

/** Germany's World Bank figures – the basis of every market's `*_vs_germany` shares. */
export interface EconomyReference {
  iso2: string;
  name: string;
  population: number;
  gdp_per_capita_usd: number;
  gdp_usd: number;
  economy_year: number;
}

export interface MarketList {
  markets: Market[];
  counts: Record<MarketStatus | "campaign_open" | "travel_unchecked", number>;
  germany: EconomyReference;
}

export type WaveStatus =
  | "checking"
  | "pending_approval"
  | "changes_requested"
  | "approved"
  | "sending"
  | "paused"
  | "completed"
  | "rejected"
  | "withdrawn"
  | "stopped";

export interface WaveProgress {
  recipients: number;
  messages: number;
  sent: number;
  drafted: number;
  pending: number;
  stopped: number;
  failed: number;
  replied: number;
  bounced: number;
  checks_pending: number;
  checks_flagged: number;
  /** Steps that carry a letter. */
  letters: number;
  letters_signed: number;
  /** Unsent letters still waiting for the owner's signature – approval is refused while > 0. */
  letters_to_sign: number;
}

/** One PDF of a letter: the server copy (`file`) and the original on the owner's Mac. */
export interface LetterFile {
  file: string;
  local_path: string | null;
  sha256: string;
  size: number;
  pages: number;
}

export interface SignedLetterFile extends LetterFile {
  attached_at: string;
}

/** A PDF letter that goes out with an email step – signed by the owner on their Mac. */
export interface OutreachLetter extends LetterFile {
  /** The filename the recipient sees, e.g. "DUX-KG-2026-01.pdf". */
  name: string;
  signature_required: boolean;
  reference: string | null;
  legal_basis: string | null;
  /** The answer is due within this many days of sending. */
  deadline_days: number | null;
  /** A copy for the owner to read (e.g. German) – never sent. */
  review_copy: LetterFile | null;
  /** The signed file the seat attached after the owner saved it. */
  signed: SignedLetterFile | null;
}

export type LetterFileKind = "letter" | "signed" | "review_copy";

export interface OutreachMessage {
  id: string;
  contact_id: string;
  company_id: string | null;
  market_iso2: string;
  recipient_email: string;
  recipient_name: string | null;
  recipient_role: string | null;
  step: number;
  delay_days: number;
  language: string;
  language_reason: string | null;
  strategy: string;
  persona_md: string | null;
  source_url: string | null;
  subject: string | null;
  body: string;
  subject_de: string | null;
  body_de: string;
  check_status: "pending" | "ok" | "flagged" | "skipped" | "error";
  check_issues: string[];
  /** Writing-rule findings – hints for the owner, never a block. */
  lint_issues: string[];
  back_translation: string | null;
  status: "pending" | "drafted" | "sent" | "stopped" | "bounced" | "failed";
  stop_reason: string | null;
  not_before: string | null;
  /** First emails only: the seat's own date (YYYY-MM-DD) – outside the content hash, the seat moves it. */
  planned_not_before: string | null;
  sent_at: string | null;
  outcome: "replied" | "bounced" | null;
  outcome_at: string | null;
  /** First emails only: inbound messages the seat marked as automatic replies. */
  auto_replies?: AutoReplyMark[];
  /** Set when this unsent email replaced one of the version before. */
  revision?: MessageRevision | null;
  /** The PDF letter attached to this step, if any. */
  letter: OutreachLetter | null;
  cc: string[];
  error: string | null;
}

export interface MessageRevision {
  state: "new" | "changed" | "unchanged";
  /** The German text before the change (state "changed"). */
  subject_de?: string | null;
  body_de?: string | null;
  /** The letter differs from the version before (added, removed or replaced). */
  letter_changed?: boolean;
  /** The CC addresses before, when this version changed them. */
  cc_before?: string[];
}

/** What a resubmission changed – set from the second version on. */
export interface WaveRevision {
  from_version: number;
  /** The owner approved an earlier version: this is an update of a running wave. */
  running: boolean;
  /** Emails that already left the mailbox and stay as they are. */
  kept: number;
  removed: { contact_id: string; name: string | null; email: string }[];
  /** The seat's note on what changed in this version and why. */
  note?: string | null;
}

export interface AutoReplyMark {
  message_id: string;
  from: string;
  received_at: string | null;
  marked_at: string;
  /** The recipient's return date (YYYY-MM-DD); no follow-up goes out before it. */
  resume_on: string | null;
}

export interface ReplyBlock {
  id: string;
  key: string;
  purpose: string;
  body_de: string;
  variants: Record<string, string>;
}

export interface StrategyStat {
  dimension: "strategy" | "language" | "role";
  value: string;
  contacted: number;
  replied: number;
  bounced: number;
  reply_rate: number | null;
}

export type WaveDelivery = "send" | "drafts";

export interface Wave {
  id: string;
  agent_id: string;
  agent_slug: string | null;
  title: string;
  summary_md: string | null;
  status: WaveStatus;
  /** "drafts": the server leaves each email as a draft; the owner sends it from Outlook. */
  delivery: WaveDelivery;
  version: number;
  content_hash: string;
  approved_at: string | null;
  revision?: WaveRevision | null;
  decision_note: string | null;
  completed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  progress?: WaveProgress;
  markets?: string[];
  languages?: string[];
  strategies?: string[];
  reply_blocks?: ReplyBlock[];
  messages?: OutreachMessage[];
  strategy_stats?: StrategyStat[];
}

export interface WaveList {
  waves: Wave[];
  counts: Partial<Record<WaveStatus, number>>;
  strategy_stats: StrategyStat[];
}

export interface OutreachSender {
  id: string;
  email_address: string | null;
  started_at: string | null;
  daily_cap_max: number;
  cap_today: number;
  sent_today: number;
  paused: boolean;
  paused_reason: string | null;
  last_sent_at: string | null;
}

/** Why an email is due when it is, before office hours or the daily limit. */
export type PlanBasis = "approval" | "planned" | "follow_up" | "return_date" | "retry";
/** What pushed it later than that. */
export type PlanDelay = "business_hours" | "daily_cap" | "queue";
/** Why an email has no time. */
export type PlanHold =
  | "your_send"
  | "after_your_send"
  | "wave_paused"
  | "mailbox_paused"
  | "sending_disabled"
  | "unscheduled"
  | "beyond_horizon";

/** One email still to go out – `services/outreach/plan.py :: serialize_item`. */
export interface PlanItem {
  message_id: string;
  wave_id: string;
  wave_title: string;
  delivery: WaveDelivery;
  contact_id: string;
  company_id: string | null;
  company_name: string | null;
  recipient_name: string | null;
  recipient_email: string;
  market_iso2: string;
  /** The recipient's IANA time zone – office hours are theirs. */
  time_zone: string;
  step: number;
  delay_days: number;
  /** Predicted send (draft delivery: when the draft appears); null while on hold. */
  at: string | null;
  earliest: string | null;
  /** Its schedule hangs off a predicted send of the step before. */
  chained: boolean;
  basis: PlanBasis;
  delays: PlanDelay[];
  hold: PlanHold | null;
}

export interface SendPlan {
  generated_at: string;
  horizon_days: number;
  sending_disabled: boolean;
  /** Timed items first (soonest on top), held ones after. */
  items: PlanItem[];
}

export interface Suppression {
  id: string;
  email: string | null;
  domain: string | null;
  reason: string;
  note: string | null;
  created_at: string | null;
}

export const B2G_KEY = ["b2g"] as const;

export const b2gApi = {
  markets: (status?: string) => api.get<MarketList>("/api/v1/markets", { status }),
  marketDecision: (
    iso2: string,
    body: { status?: MarketStatus; campaign_excluded?: boolean; reason?: string; name?: string },
  ) => api.post<Market>(`/api/v1/markets/${encodeURIComponent(iso2)}/decision`, body),

  waves: (status?: string) => api.get<WaveList>("/api/v1/outreach/waves", { status }),
  wave: (id: string) => api.get<Wave>(`/api/v1/outreach/waves/${id}`),
  approve: (id: string, contentHash: string, delivery: WaveDelivery = "send") =>
    api.post<Wave>(`/api/v1/outreach/waves/${id}/approve`, { content_hash: contentHash, delivery }),
  requestChanges: (id: string, note: string) =>
    api.post<Wave>(`/api/v1/outreach/waves/${id}/request-changes`, { note }),
  reject: (id: string, note?: string) => api.post<Wave>(`/api/v1/outreach/waves/${id}/reject`, { note }),
  pause: (id: string) => api.post<Wave>(`/api/v1/outreach/waves/${id}/pause`),
  resume: (id: string) => api.post<Wave>(`/api/v1/outreach/waves/${id}/resume`),
  stop: (id: string, note?: string) => api.post<Wave>(`/api/v1/outreach/waves/${id}/stop`, { note }),

  plan: () => api.get<SendPlan>("/api/v1/outreach/plan"),

  senders: () => api.get<OutreachSender[]>("/api/v1/outreach/senders"),
  registerSender: (mailbox_address: string) =>
    api.post<OutreachSender>("/api/v1/outreach/senders", { mailbox_address }),
  killSwitch: (paused: boolean, reason?: string) =>
    api.post<OutreachSender[]>("/api/v1/outreach/kill-switch", { paused, reason }),
  suppressions: () => api.get<Suppression[]>("/api/v1/outreach/suppressions"),
  liftSuppression: (id: string) => api.delete<void>(`/api/v1/outreach/suppressions/${id}`),
};

/** The server copy of a step's letter PDF – owner-only (session cookie), served inline; open it in a new tab. */
export function letterFileUrl(messageId: string, kind: LetterFileKind): string {
  return `/api/v1/outreach/messages/${encodeURIComponent(messageId)}/files/${kind}`;
}

/** Every market by ISO code – what a country label shows on hover. Shares the
 * markets tab's cache entry. */
export function useMarketIndex(): Map<string, Market> {
  const q = useQuery({
    queryKey: [...B2G_KEY, "markets"],
    queryFn: () => b2gApi.markets(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return useMemo(() => new Map((q.data?.markets ?? []).map((m) => [m.iso2, m])), [q.data]);
}

/** Lower-cased addresses of the caller's outreach mailboxes — where an
 * approved email draft is sent by the server instead of drafted. */
export function useOutreachMailboxes(): Set<string> {
  const q = useQuery({
    queryKey: [...B2G_KEY, "senders"],
    queryFn: b2gApi.senders,
    staleTime: 60_000,
    retry: false,
  });
  return new Set((q.data ?? []).map((s) => (s.email_address ?? "").toLowerCase()).filter(Boolean));
}
